import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  borrowing_id: string | null;
  content: string;
  read_at: string | null;
  created_at: string;
  sender?: { full_name: string } | null;
}

export interface Conversation {
  userId: string;
  userName: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  borrowingId: string | null;
}

export const DEFAULT_SUPPORT_USER_ID = "d4ac14ff-adba-4b22-9de7-fd3d7b49d43c";
export const LOCAL_SEED_ADMIN_USER_ID = "bbbbbbbb-bbbb-4000-8000-000000000001";

export function isSupportUserId(id: string | null | undefined): boolean {
  return (
    id === DEFAULT_SUPPORT_USER_ID ||
    id === LOCAL_SEED_ADMIN_USER_ID
  );
}

export function useConversations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["conversations", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<Conversation[]> => {
      const { data, error } = await supabase
        .from("messages")
        .select(
          "id, sender_id, recipient_id, content, created_at, read_at, borrowing_id, sender:sender_id(full_name), recipient:recipient_id(full_name)"
        )
        .or(`sender_id.eq.${user!.id},recipient_id.eq.${user!.id}`)
        .order("created_at", { ascending: false })
        .limit(200);

      if (error) throw error;

      const convMap = new Map<string, Conversation>();

      for (const msg of (data ?? []) as any[]) {
        const isSentByMe = msg.sender_id === user!.id;
        const otherId = isSentByMe ? msg.recipient_id : msg.sender_id;
        const rawName = isSentByMe
          ? (msg.recipient as any)?.full_name
          : (msg.sender as any)?.full_name;
        const otherName =
          typeof rawName === "string" && rawName.trim() && !rawName.includes("@")
            ? rawName.trim()
            : isSupportUserId(otherId)
            ? "Soporte Brickshare"
            : "Usuario";

        if (!convMap.has(otherId)) {
          convMap.set(otherId, {
            userId: otherId,
            userName: otherName,
            lastMessage: msg.content ?? "",
            lastMessageAt: msg.created_at,
            unreadCount: 0,
            borrowingId: msg.borrowing_id,
          });
        }

        if (msg.recipient_id === user!.id && !msg.read_at) {
          convMap.get(otherId)!.unreadCount++;
        }

        // Actualizar nombre del otro usuario si lo tenemos
        const conv = convMap.get(otherId)!;
        if (otherName && otherName !== "Usuario" && (!conv.userName || conv.userName === "Usuario")) {
          conv.userName = otherName;
        }
      }

      return Array.from(convMap.values()).sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
      );
    },
  });
}

export function useMessages(otherUserId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["messages", user?.id, otherUserId],
    enabled: !!user?.id && !!otherUserId,
    queryFn: async (): Promise<Message[]> => {
      const targetIds = isSupportUserId(otherUserId)
        ? `${DEFAULT_SUPPORT_USER_ID},${LOCAL_SEED_ADMIN_USER_ID}`
        : otherUserId;

      const orFilter = isSupportUserId(otherUserId)
        ? `and(sender_id.eq.${user!.id},recipient_id.in.(${targetIds})),and(sender_id.in.(${targetIds}),recipient_id.eq.${user!.id})`
        : `and(sender_id.eq.${user!.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${user!.id})`;

      const { data, error } = await supabase
        .from("messages")
        .select("*, sender:sender_id(full_name)")
        .or(orFilter)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as Message[];
    },
  });
}

export function useSendMessage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return async (recipientId: string, content: string, borrowingId?: string) => {
    const trimmed = content.trim();
    if (!user?.id || !recipientId || !trimmed) return null;

    const { data, error } = await supabase.functions.invoke("send-message", {
      body: {
        recipient_id: recipientId,
        content: trimmed,
        borrowing_id: borrowingId ?? null,
      },
    });

    if (error || data?.success === false) {
      // Fallback directo a tabla `messages` (RLS `messages_insert_sender`) y resolución de soporte en dev
      let resolvedRecipient = recipientId;
      if (isSupportUserId(recipientId)) {
        const { data: adminRow } = await supabase
          .from("users")
          .select("id")
          .or(`id.eq.${DEFAULT_SUPPORT_USER_ID},id.eq.${LOCAL_SEED_ADMIN_USER_ID},role.eq.admin`)
          .neq("id", user.id)
          .limit(1)
          .maybeSingle();
        if (adminRow?.id) {
          resolvedRecipient = adminRow.id;
        }
      }

      const { data: inserted, error: insertErr } = await supabase
        .from("messages")
        .insert({
          sender_id: user.id,
          recipient_id: resolvedRecipient,
          content: trimmed,
          borrowing_id: borrowingId ?? null,
        })
        .select("id, created_at")
        .single();

      if (insertErr) {
        toast.error(data?.error ?? insertErr.message ?? error?.message ?? "Error al enviar mensaje");
        return null;
      }

      queryClient.invalidateQueries({ queryKey: ["messages", user.id, recipientId] });
      queryClient.invalidateQueries({ queryKey: ["messages", user.id, resolvedRecipient] });
      queryClient.invalidateQueries({ queryKey: ["conversations", user.id] });
      return inserted;
    }

    queryClient.invalidateQueries({ queryKey: ["messages", user?.id, recipientId] });
    queryClient.invalidateQueries({ queryKey: ["conversations", user?.id] });
    return data.data;
  };
}

/**
 * useMarkMessagesRead — Marca como leídos los mensajes RECIBIDOS por el
 * usuario autenticado que provienen de `otherUserId` (RPC seguro:
 * únicamente puede tocar filas con recipient_id = auth.uid()).
 *
 * Activa los checkmarks de lectura en el panel usuario (y admin) y
 * limpia los badges de conversaciones con el otro usuario.
 *
 * Devuelve el nº de mensajes marcados (0 si no había nada nuevo).
 */
export function useMarkMessagesRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return async (otherUserId: string): Promise<number> => {
    if (!user?.id) return 0;

    const { data, error } = await supabase.rpc("mark_messages_read", {
      p_other_user: otherUserId,
    });

    if (error) {
      console.error("[mark_messages_read]", error);
      return 0;
    }

    const marked = (data as number) ?? 0;
    if (marked > 0) {
      // Refrescar contadores del panel usuario y del panel admin (badge "P2P · Chat")
      queryClient.invalidateQueries({ queryKey: ["conversations", user.id] });
      queryClient.invalidateQueries({ queryKey: ["admin_conversations", user.id] });
      queryClient.invalidateQueries({ queryKey: ["admin_unread", user.id] });
    }
    return marked;
  };
}